import {useMemo, useEffect, useState, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';

// react-table components
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
} from 'react-table';

// @mui material components
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Icon from '@mui/material/Icon';
import Autocomplete from '@mui/material/Autocomplete';

// Material Dashboard 2 React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDPagination from 'components/MDPagination';

// Material Dashboard 2 React example components
import DataTableHeadCell from 'examples/Tables/DataTable/DataTableHeadCell';
import DataTableBodyCell from 'examples/Tables/DataTable/DataTableBodyCell';

// Isolated on purpose: this owns the keystroke-by-keystroke input state itself, so typing
// only re-renders this tiny box — never the parent DataTable (all its rows, all its Cell
// components) — until the debounce actually fires and `onSearch` runs. Previously `search`
// lived in DataTable's own state, so every keystroke re-rendered the entire table body on
// every table using `canSearch`, which is what made typing feel laggy/unresponsive.
function TableSearchInput({onSearch}) {
  const [value, setValue] = useState('');

  const debouncedSearch = useAsyncDebounce(onSearch, 150);

  const handleChange = useCallback(
    ({currentTarget}) => {
      setValue(currentTarget.value);
      debouncedSearch(currentTarget.value);
    },
    [debouncedSearch],
  );

  return (
    <MDInput
      placeholder="Search..."
      value={value}
      size="small"
      fullWidth
      onChange={handleChange}
    />
  );
}

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  hoverEffect,
  onPageChange,
  onRowClick,
}) {
  const data = useMemo(() => table.rows || [], [table.rows]);

  // When entriesPerPage is false, the caller has already paginated (e.g.
  // server-side) and is handing us exactly one page's rows — react-table
  // would otherwise still default to paging 10 at a time internally,
  // producing a second, redundant pagination control inside this table.
  // pageSize is only read as initialState (first render, when data may still
  // be empty), so use a fixed large size rather than data.length.
  const defaultValue = useMemo(() => {
    if (entriesPerPage?.defaultValue) return entriesPerPage.defaultValue;
    if (!entriesPerPage) return 10000;
    return 10;
  }, [entriesPerPage]);

  const entries = useMemo(() => {
    return entriesPerPage?.entries
      ? entriesPerPage.entries.map(el => el.toString())
      : ['5', '10', '15', '20', '25'];
  }, [entriesPerPage?.entries]);

  const columns = useMemo(() => table.columns || [], [table.columns]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: defaultValue,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: {pageIndex, pageSize},
  } = tableInstance;

  const previousPaginationRef = useRef({
    pageIndex,
    pageSize,
  });

  const handleSearch = useCallback(
    value => {
      setGlobalFilter(value || undefined);
    },
    [setGlobalFilter],
  );

  const setEntriesPerPage = useCallback(
    value => {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue > 0) {
        setPageSize(numValue);
      }
    },
    [setPageSize],
  );

  const setSortedValue = useCallback(
    column => {
      if (isSorted && column.isSorted) {
        return column.isSortedDesc ? 'desc' : 'asce';
      } else if (isSorted) {
        return 'none';
      }
      return false;
    },
    [isSorted],
  );

  const renderPagination = useMemo(() => {
    return pageOptions.map(option => (
      <MDPagination
        item
        key={option}
        onClick={() => gotoPage(Number(option))}
        active={pageIndex === option}>
        {option + 1}
      </MDPagination>
    ));
  }, [pageOptions, pageIndex, gotoPage]);

  const handleInputPagination = useCallback(
    ({target: {value}}) => {
      const numValue = Number(value);
      if (numValue > pageOptions.length || numValue < 1) {
        gotoPage(0);
      } else {
        gotoPage(numValue - 1);
      }
    },
    [pageOptions.length, gotoPage],
  );

  const customizedPageOptions = useMemo(() => {
    return pageOptions.map(option => option + 1);
  }, [pageOptions]);

  const entriesInfo = useMemo(() => {
    const entriesStart =
      pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;
    let entriesEnd;

    if (pageIndex === 0) {
      entriesEnd = Math.min(pageSize, rows.length);
    } else if (pageIndex === pageOptions.length - 1) {
      entriesEnd = rows.length;
    } else {
      entriesEnd = Math.min(pageSize * (pageIndex + 1), rows.length);
    }

    return {entriesStart, entriesEnd};
  }, [pageIndex, pageSize, rows.length, pageOptions.length]);

  useEffect(() => {
    const previousPagination = previousPaginationRef.current;
    const paginationChanged =
      previousPagination.pageIndex !== pageIndex ||
      previousPagination.pageSize !== pageSize;

    if (paginationChanged && typeof onPageChange === 'function') {
      onPageChange({pageIndex, pageSize});
    }

    previousPaginationRef.current = {
      pageIndex,
      pageSize,
    };
  }, [onPageChange, pageIndex, pageSize]);

  // Memoized so re-renders of DataTable that don't actually change the filtered/sorted/
  // paginated result (e.g. re-renders bubbling down from a parent) don't force the whole
  // table body to re-render. Real search-typing performance is handled by keeping the
  // keystroke state entirely inside <TableSearchInput> above, so DataTable itself only
  // re-renders once the debounced global filter actually changes.
  const tableContent = useMemo(
    () => (
      <TableContainer sx={{boxShadow: 'none'}}>
        <Table {...getTableProps()}>
          <MDBox component="thead">
            {headerGroups.map((headerGroup, key) => (
              <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, idx) => (
                  <DataTableHeadCell
                    key={idx}
                    {...column.getHeaderProps(
                      isSorted && column.getSortByToggleProps(),
                    )}
                    width={column.width ? column.width : 'auto'}
                    align={column.align ? column.align : 'left'}
                    sorted={setSortedValue(column)}>
                    {column.render('Header')}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </MDBox>
          <TableBody {...getTableBodyProps()}>
            {page.map((row, key) => {
              prepareRow(row);
              const rowProps = row.getRowProps();
              return (
                <TableRow
                  hover={hoverEffect}
                  key={rowProps.key || row.id || key}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  sx={{
                    ...row.original?.rowSx, // Fixed: Added optional chaining
                    cursor: onRowClick ? 'pointer' : undefined,
                  }}
                  {...rowProps}>
                  {row.cells.map((cell, idx) => {
                    const cellProps = cell.getCellProps();
                    return (
                      <DataTableBodyCell
                        key={
                          cellProps.key || `${row.id}-${cell.column.id || idx}`
                        }
                        noBorder={noEndBorder && page.length - 1 === key}
                        align={cell.column.align ? cell.column.align : 'left'}
                        sx={cell.column?.cellSx} // Fixed: Added optional chaining
                        padding={
                          cell.column.padding !== undefined
                            ? cell.column.padding
                            : 'auto'
                        }
                        {...cellProps}>
                        {cell.render('Cell')}
                      </DataTableBodyCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headerGroups, page, isSorted, noEndBorder, hoverEffect, setSortedValue, onRowClick],
  );

  return (
    // Only the <TableContainer> below (wrapping just the <Table>) scrolls horizontally
    // when columns overflow — the toolbar and footer sit outside it so they stay put
    // instead of sliding out of view along with the table body.
    <MDBox sx={{boxShadow: 'none'}}>
      {(entriesPerPage || canSearch) && (
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(newValue);
                }}
                size="small"
                sx={{width: '5rem'}}
                renderInput={params => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <TableSearchInput onSearch={handleSearch} />
            </MDBox>
          )}
        </MDBox>
      )}
      {tableContent}

      <MDBox
        display="flex"
        flexDirection={{xs: 'column', sm: 'row'}}
        justifyContent="space-between"
        alignItems={{xs: 'flex-start', sm: 'center'}}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}>
        {showTotalEntries && (
          <MDBox mb={{xs: 3, sm: 0}}>
            <MDTypography
              variant="button"
              color="secondary"
              fontWeight="regular">
              Showing {entriesInfo.entriesStart} to {entriesInfo.entriesEnd} of{' '}
              {rows.length} entries
            </MDTypography>
          </MDBox>
        )}
        {pageOptions.length > 1 && (
          <MDPagination
            variant={pagination?.variant || 'gradient'}
            color={pagination?.color || 'info'}>
            {canPreviousPage && (
              <MDPagination item onClick={() => previousPage()}>
                <Icon sx={{fontWeight: 'bold'}}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{
                    type: 'number',
                    min: 1,
                    max: customizedPageOptions.length,
                  }}
                  value={customizedPageOptions[pageIndex] || 1}
                  onChange={handleInputPagination}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <MDPagination item onClick={() => nextPage()}>
                <Icon sx={{fontWeight: 'bold'}}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </MDBox>
  );
}

DataTable.defaultProps = {
  entriesPerPage: {defaultValue: 10, entries: [5, 10, 15, 20, 25]},
  canSearch: false,
  showTotalEntries: true,
  pagination: {variant: 'gradient', color: 'info'},
  isSorted: true,
  noEndBorder: false,
  hoverEffect: false,
  onPageChange: undefined,
  onRowClick: undefined,
};

DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(['contained', 'gradient']),
    color: PropTypes.oneOf([
      'primary',
      'secondary',
      'info',
      'success',
      'warning',
      'error',
      'dark',
      'light',
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  hoverEffect: PropTypes.bool,
  onPageChange: PropTypes.func,
  onRowClick: PropTypes.func,
};

export default DataTable;
